import { useState, useEffect, useCallback } from 'react';
import masterDataService from '../services/masterDataService';

// Hook for using master data values by category
export const useMasterData = (categoryName) => {
  const [values, setValues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchValues = useCallback(async () => {
    if (!categoryName) {
      setValues([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await masterDataService.getValuesByCategory(categoryName);
      setValues(data);
    } catch (err) {
      console.error(`Error fetching master data for ${categoryName}:`, err);
      setError(err.message || 'Failed to fetch master data');
      setValues([]);
    } finally {
      setLoading(false);
    }
  }, [categoryName]);

  useEffect(() => {
    fetchValues();
  }, [fetchValues]);

  const refresh = useCallback(() => {
    return fetchValues();
  }, [fetchValues]);

  return {
    values,
    loading,
    error,
    refresh
  };
};

// Hook for using master data values as simple array
export const useMasterDataArray = (categoryName) => {
  const [values, setValues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchValues = useCallback(async () => {
    if (!categoryName) {
      setValues([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await masterDataService.getValuesArray(categoryName);
      setValues(data);
    } catch (err) {
      console.error(`Error fetching master data array for ${categoryName}:`, err);
      setError(err.message || 'Failed to fetch master data');
      setValues([]);
    } finally {
      setLoading(false);
    }
  }, [categoryName]);

  useEffect(() => {
    fetchValues();
  }, [fetchValues]);

  const refresh = useCallback(() => {
    return fetchValues();
  }, [fetchValues]);

  return {
    values,
    loading,
    error,
    refresh
  };
};

// Hook for using master data values as options array
export const useMasterDataOptions = (categoryName) => {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchOptions = useCallback(async () => {
    if (!categoryName) {
      setOptions([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await masterDataService.getOptionsArray(categoryName);
      setOptions(data);
    } catch (err) {
      console.error(`Error fetching master data options for ${categoryName}:`, err);
      setError(err.message || 'Failed to fetch master data');
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, [categoryName]);

  useEffect(() => {
    fetchOptions();
  }, [fetchOptions]);

  const refresh = useCallback(() => {
    return fetchOptions();
  }, [fetchOptions]);

  return {
    options,
    loading,
    error,
    refresh
  };
};

// Hook for using all master data values
export const useAllMasterData = () => {
  const [allValues, setAllValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAllValues = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await masterDataService.getAllValues();
      setAllValues(data);
    } catch (err) {
      console.error('Error fetching all master data:', err);
      setError(err.message || 'Failed to fetch master data');
      setAllValues({});
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAllValues();
  }, [fetchAllValues]);

  const refresh = useCallback(() => {
    return fetchAllValues();
  }, [fetchAllValues]);

  return {
    allValues,
    loading,
    error,
    refresh
  };
};

// Hook for using master data categories
export const useMasterDataCategories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchCategories = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await masterDataService.getCategories();
      setCategories(data);
    } catch (err) {
      console.error('Error fetching master data categories:', err);
      setError(err.message || 'Failed to fetch categories');
      setCategories([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const refresh = useCallback(() => {
    return fetchCategories();
  }, [fetchCategories]);

  return {
    categories,
    loading,
    error,
    refresh
  };
};

// Hook for searching master data values
export const useMasterDataSearch = () => {
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState(null);

  const search = useCallback(async (query, category = null, limit = 20) => {
    if (!query || query.trim().length === 0) {
      setSearchResults([]);
      return;
    }

    try {
      setSearching(true);
      setSearchError(null);
      const results = await masterDataService.searchValues(query, category, limit);
      setSearchResults(results);
    } catch (err) {
      console.error('Error searching master data:', err);
      setSearchError(err.message || 'Failed to search master data');
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  }, []);

  const clearSearch = useCallback(() => {
    setSearchResults([]);
    setSearchError(null);
  }, []);

  return {
    searchResults,
    searching,
    searchError,
    search,
    clearSearch
  };
};

// Hook for master data service utilities
export const useMasterDataUtils = () => {
  const [healthStatus, setHealthStatus] = useState(null);
  const [cacheStats, setCacheStats] = useState(null);

  const checkHealth = useCallback(async () => {
    try {
      const status = await masterDataService.healthCheck();
      setHealthStatus(status);
      return status;
    } catch (err) {
      console.error('Error checking master data service health:', err);
      const errorStatus = { status: 'error', message: 'Health check failed' };
      setHealthStatus(errorStatus);
      return errorStatus;
    }
  }, []);

  const getCacheStats = useCallback(() => {
    const stats = masterDataService.getCacheStats();
    setCacheStats(stats);
    return stats;
  }, []);

  const clearCache = useCallback(() => {
    masterDataService.clearCache();
    setCacheStats(masterDataService.getCacheStats());
  }, []);

  const refreshAll = useCallback(async () => {
    try {
      await masterDataService.refreshAll();
      setCacheStats(masterDataService.getCacheStats());
    } catch (err) {
      console.error('Error refreshing all master data:', err);
      throw err;
    }
  }, []);

  return {
    healthStatus,
    cacheStats,
    checkHealth,
    getCacheStats,
    clearCache,
    refreshAll
  };
}; 